import React, { useState, useEffect } from 'react';
import { Plus, Save, Eye, Link, Palette, Camera, Circle, Shield, ClipboardList, Eye as ViewIcon, BarChart3, Trash2, Loader2, Copy } from 'lucide-react';
import styled from 'styled-components';
import { LandingPageConfig, ButtonConfig } from '../types';
import { apiService, RetryStatusCallback } from '../services/api';
import ButtonEditor from './ButtonEditor';
import ThemeSelector from './ThemeSelector';
import FontSelector from './FontSelector';
import PreviewModal from './PreviewModal';
import StatsModal from './StatsModal';
import DuplicateModal from './DuplicateModal';

const BuilderContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const BuilderCard = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  text-align: center;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
  font-family: 'Inter', sans-serif;
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: 1.1rem;
  opacity: 0.9;
  font-family: 'Inter', sans-serif;
`;

const Content = styled.div`
  padding: 40px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter', sans-serif;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ImageUpload = styled.div`
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f8fafc;

  &:hover {
    border-color: #667eea;
    background: #f0f4ff;
  }
`;

const ImagePreview = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const PreviewImage = styled.img`
  max-width: 200px;
  max-height: 200px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Actions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 40px;
`;

// Unified Button System
const BaseButton = styled.button<{
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost'
  size?: 'small' | 'medium' | 'large'
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  /* Size variants */
  ${({ size }) => {
    switch (size) {
      case 'small':
        return `
          padding: 8px 12px;
          font-size: 12px;
          min-height: 32px;
        `;
      case 'large':
        return `
          padding: 16px 24px;
          font-size: 16px;
          min-height: 48px;
        `;
      default: // medium
        return `
          padding: 12px 16px;
          font-size: 14px;
          min-height: 40px;
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Color variants */
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #667eea;
          border: 2px solid #667eea;
          &:hover:not(:disabled) {
            background: #667eea;
            color: white;
          }
        `;
      case 'success':
        return `
          background: #48bb78;
          color: white;
          &:hover:not(:disabled) {
            background: #38a169;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
          }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover:not(:disabled) {
            background: #dc2626;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: #6b7280;
          border: 1px solid #e5e7eb;
          &:hover:not(:disabled) {
            background: #f9fafb;
            border-color: #d1d5db;
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
        `;
    }
  }}
`;

const PagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const PageItem = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${props => props.isActive ? '#f0f4ff' : '#f8fafc'};
  border: 2px solid ${props => props.isActive ? '#667eea' : '#e2e8f0'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const PageInfo = styled.div`
  flex: 1;
  cursor: pointer;
`;

const PageName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  font-family: 'Inter', sans-serif;
`;

const PageUrl = styled.div`
  font-size: 14px;
  color: #667eea;
  font-weight: 500;
  margin-bottom: 2px;
  font-family: 'Inter', sans-serif;
`;

const PageDate = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-family: 'Inter', sans-serif;
`;

const PageActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ConnectionStatus = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  background: ${props => props.isVisible ? '#f59e0b' : 'transparent'};
  color: white;
  padding: ${props => props.isVisible ? '12px 20px' : '0'};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  z-index: 1000;
  transition: all 0.3s ease;
  opacity: ${props => props.isVisible ? 1 : 0};
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
  max-width: 300px;
`;

const StatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusMain = styled.div`
  font-size: 14px;
`;

const StatusSub = styled.div`
  font-size: 12px;
  opacity: 0.9;
`;


interface BuilderProps {
  onLogout?: () => void;
}

const Builder: React.FC<BuilderProps> = ({ onLogout }) => {
  const [config, setConfig] = useState<LandingPageConfig>({
    id: 'page1',
    slug: 'my-landing-page',
    name: 'My Landing Page',
    imageUrl: '',
    displayName: '',
    bio: '',
    font: 'clean',
    theme: 'soft-pinks',
    imageCropStyle: 'circle-medium',
    buttons: [],
    fallback: {
      title: "Don't wanna join yet?",
      imageUrl: '',
      buttonText: 'Get Free Trial',
      buttonLink: '',
    },
    buttonPosition: 'floating',
    createdAt: new Date(),
    updatedAt: new Date(),
  });

  const [showPreview, setShowPreview] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<LandingPageConfig | null>(null);
  const [selectedStatsPage, setSelectedStatsPage] = useState<LandingPageConfig | null>(null);
  const [savedPages, setSavedPages] = useState<LandingPageConfig[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<{
    isRetrying: boolean;
    attempt: number;
    maxAttempts: number;
    delay: number;
  } | null>(null);

  useEffect(() => {
    // Set up retry status callback
    const retryCallback: RetryStatusCallback = (status) => {
      setConnectionStatus(status.isRetrying ? status : null);
    };
    apiService.setRetryStatusCallback(retryCallback);

    loadAllSavedPages();

    // Cleanup callback on unmount
    return () => {
      apiService.setRetryStatusCallback(null);
    };
  }, []);

  const loadAllSavedPages = async () => {
    try {
      const pages = await apiService.loadAllLandingPages();
      setSavedPages(pages);
    } catch (error) {
      console.error('Failed to load pages:', error);
    }
  };

  const updateConfig = (updates: Partial<LandingPageConfig>) => {
    setConfig(prev => ({
      ...prev,
      ...updates,
      updatedAt: new Date(),
    }));
  };

  const addButton = () => {
    const newButton: ButtonConfig = {
      id: `button-${Date.now()}`,
      text: 'Click Me',
      link: '',
      animation: 'pulse',
    };
    updateConfig({ buttons: [...config.buttons, newButton] });
  };

  const updateButton = (id: string, updates: Partial<ButtonConfig>) => {
    updateConfig({
      buttons: config.buttons.map(btn =>
        btn.id === id ? { ...btn, ...updates } : btn
      ),
    });
  };

  const removeButton = (id: string) => {
    updateConfig({
      buttons: config.buttons.filter(btn => btn.id !== id),
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (warn at 5MB, block at 15MB)
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > 15) {
        alert(`Image is too large (${sizeInMB.toFixed(1)}MB). Maximum size is 15MB.`);
        return;
      } else if (sizeInMB > 5) {
        alert(`Large image (${sizeInMB.toFixed(1)}MB). Consider using a smaller image for better performance, or use an external URL.`);
      }

      try {
        const imageUrl = await apiService.uploadImage(file);
        updateConfig({ imageUrl });
      } catch (error) {
        console.error('Image upload failed:', error);
        alert(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };


  const saveConfig = async () => {
    // Validate slug
    if (!config.slug || config.slug.trim() === '' || config.slug.trim() === '-') {
      alert('Please set a valid custom URL for your landing page (letters, numbers, and dashes only)');
      return;
    }

    try {
      // Check if this page already exists in the database by trying to load all pages
      // This ensures we have the most up-to-date data
      const allPages = await apiService.loadAllLandingPages();
      const existingPageById = allPages.find(p => p.id === config.id);
      const conflictingPage = allPages.find(p => p.slug === config.slug && p.id !== config.id);

      if (conflictingPage) {
        alert('This URL slug is already taken by another page. Please choose a different one.');
        return;
      }

      const updatedConfig = { ...config, updatedAt: new Date() };

      // If this is a new page (no existing page with this id), set id = slug
      // If editing existing page, keep the id as is
      if (!existingPageById && config.id !== config.slug) {
        updatedConfig.id = config.slug;
      }

      await apiService.saveLandingPage(updatedConfig);
      setConfig(updatedConfig);

      await loadAllSavedPages(); // Refresh the list
      alert(`Landing page saved successfully! Access it at: /${config.slug}`);
    } catch (error) {
      console.error('Save error:', error);
      // If the error is about slug already existing, show a clearer message
      if (error instanceof Error && error.message.includes('Slug already exists')) {
        alert('This URL slug is already taken by another page. Please choose a different one.');
      } else {
        alert(`Failed to save landing page: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };


  const deleteConfig = async (pageSlug: string) => {
    if (window.confirm(`Are you sure you want to delete the landing page "${pageSlug}"?`)) {
      try {
        await apiService.deleteLandingPage(pageSlug);
        await loadAllSavedPages();

        // If we deleted the current page, load another one or reset
        if (config.slug === pageSlug) {
          const remainingPages = savedPages.filter(p => p.slug !== pageSlug);
          if (remainingPages.length > 0) {
            setConfig(remainingPages[0]);
          } else {
            // Reset to default
            setConfig({
              id: 'new-page',
              slug: 'new-landing-page',
              name: 'My Landing Page',
              imageUrl: '',
              displayName: '',
              bio: '',
              font: 'clean',
              theme: 'soft-pinks',
              imageCropStyle: 'circle-medium',
              buttons: [],
              fallback: {
                title: "Don't wanna join yet?",
                imageUrl: '',
                buttonText: 'Get Free Trial',
                buttonLink: '',
              },
              buttonPosition: 'floating',
              createdAt: new Date(),
              updatedAt: new Date(),
            });
          }
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete landing page. Please try again.');
      }
    }
  };

  const createNewPage = () => {
    const newConfig: LandingPageConfig = {
      id: `new-page-${Date.now()}`,
      slug: `new-landing-page-${Date.now()}`,
      name: 'My Landing Page',
      imageUrl: '',
      displayName: '',
      bio: '',
      font: 'clean',
      theme: 'soft-pinks',
      imageCropStyle: 'circle-medium',
      buttons: [],
      fallback: {
        title: "Don't wanna join yet?",
        imageUrl: '',
        buttonText: 'Get Free Trial',
        buttonLink: '',
      },
      buttonPosition: 'floating',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setConfig(newConfig);
  };

  const handleDuplicate = async (sourceSlug: string) => {
    try {
      // Load the source page fresh
      const sourcePage = await apiService.loadLandingPageForEditing(sourceSlug);
      if (!sourcePage) {
        alert('Failed to load source page for duplication');
        return;
      }

      // Load all pages for conflict checking
      const allPages = await apiService.loadAllLandingPages();

      setDuplicateSource(sourcePage);
      setSavedPages(allPages); // Update cached pages for conflict checking
      setShowDuplicate(true);
    } catch (error) {
      console.error('Failed to prepare duplicate:', error);
      alert('Failed to prepare page for duplication');
    }
  };

  const handleDuplicateSubmit = async (name: string, slug: string) => {
    if (!duplicateSource) return;

    try {
      // Create duplicate config
      const duplicateConfig: LandingPageConfig = {
        ...duplicateSource,
        id: `duplicate-${Date.now()}`, // New unique ID
        slug: slug,
        name: name,
        createdAt: new Date(),
        updatedAt: new Date(),
        stats: undefined // Don't copy stats for fresh analytics
      };

      // Save the duplicate
      await apiService.saveLandingPage(duplicateConfig);

      // Switch to editing the new duplicate
      setConfig(duplicateConfig);

      // Refresh the pages list
      await loadAllSavedPages();

      alert(`Page duplicated successfully! Now editing: /${slug}`);
    } catch (error) {
      console.error('Duplicate creation failed:', error);
      throw error; // Re-throw for modal to handle
    }
  };

  return (
    <BuilderContainer>
      <ConnectionStatus isVisible={connectionStatus?.isRetrying || false}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <StatusText>
          <StatusMain>Waiting for server...</StatusMain>
          <StatusSub>
            Retry {connectionStatus?.attempt || 0}/{connectionStatus?.maxAttempts || 0}
            {connectionStatus?.delay ? ` (${connectionStatus.delay}s)` : ''}
          </StatusSub>
        </StatusText>
      </ConnectionStatus>
      <BuilderCard>
        <Header>
          <HeaderTitle>Landing Page Builder</HeaderTitle>
          <HeaderSubtitle>by @basednmd on tg</HeaderSubtitle>
        </Header>

        <Content>
          <Section>
            <SectionTitle><Link size={20} /> Landing Page Settings</SectionTitle>
            <InputGroup>
              <Label>Custom URL</Label>
              <Input
                type="text"
                value={config.slug}
                onChange={(e) => {
                  let slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
                  // Ensure slug is never empty and has at least one letter/number
                  if (slug === '' && e.target.value.trim() !== '') {
                    slug = 'landing-page-' + Date.now();
                  }
                  updateConfig({ slug });
                }}
                placeholder="my-landing-page"
              />
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Your landing page will be accessible at: /{config.slug}
              </small>
            </InputGroup>
            <InputGroup>
              <Label>Page Name (for your reference)</Label>
              <Input
                type="text"
                value={config.name}
                onChange={(e) => updateConfig({ name: e.target.value })}
                placeholder="Summer Sale Landing Page"
              />
            </InputGroup>

            <InputGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={config.enableGeoTag || false}
                  onChange={(e) => updateConfig({ enableGeoTag: e.target.checked })}
                />
                Enable Geo Tag
              </Label>
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Shows "Hey we are close! I'm in (City Name) as well!" below "I'm online!" based on visitor's IP.
              </small>
            </InputGroup>
          </Section>

          <Section>
            <SectionTitle><Palette size={20} /> Theme & Styling</SectionTitle>
            <ThemeSelector
              selectedTheme={config.theme}
              onThemeChange={(theme) => updateConfig({ theme: theme as any })}
            />
            <FontSelector
              selectedFont={config.font}
              onFontChange={(font) => updateConfig({ font: font as any })}
            />
          </Section>

          <Section>
            <SectionTitle><Camera size={20} /> Profile</SectionTitle>
            <InputGroup>
              <Label>Display Name</Label>
              <Input
                type="text"
                value={config.displayName}
                onChange={(e) => updateConfig({ displayName: e.target.value })}
                placeholder="Enter your name"
              />
            </InputGroup>

            <InputGroup>
              <Label>Bio</Label>
              <Textarea
                value={config.bio}
                onChange={(e) => updateConfig({ bio: e.target.value })}
                placeholder="Tell visitors about yourself..."
              />
            </InputGroup>

            <InputGroup>
              <Label>Profile Image/GIF</Label>
              <ImageUpload>
                <input
                  type="file"
                  accept="image/*,.gif"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  id="image-upload"
                />
                <label htmlFor="image-upload" style={{ cursor: 'pointer' }}>
                  <Camera size={16} style={{ marginRight: '8px' }} />
                  Click to upload image or GIF
                </label>
              </ImageUpload>
              {config.imageUrl && (
                <ImagePreview>
                  <PreviewImage src={config.imageUrl} alt="Profile preview" />
                </ImagePreview>
              )}
            </InputGroup>

            <InputGroup>
              <Label>Image Crop Style</Label>
              <select
                value={config.imageCropStyle}
                onChange={(e) => updateConfig({ imageCropStyle: e.target.value as any })}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="circle-medium">Medium Circle (240px) - Classic look</option>
                <option value="circle-large">Large Circle (280px) - More prominent</option>
                <option value="rectangle-soft">Rectangle with Soft Edges (240x320px) - Modern look</option>
              </select>
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Choose how your profile image is cropped and displayed
              </small>
            </InputGroup>
          </Section>

          <Section>
            <SectionTitle><Circle size={20} /> Buttons</SectionTitle>
            <InputGroup>
              <Label>Button Position</Label>
              <select
                value={config.buttonPosition}
                onChange={(e) => updateConfig({ buttonPosition: e.target.value as 'settled' | 'floating' })}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: 'Inter, sans-serif',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="settled">Settled - Buttons below bio in normal flow</option>
                <option value="floating">Floating - Buttons move with page scroll</option>
              </select>
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Choose how buttons appear on your landing page
              </small>
            </InputGroup>
            <ButtonsContainer>
              {config.buttons.map((button) => (
                <ButtonEditor
                  key={button.id}
                  button={button}
                  onUpdate={(updates) => updateButton(button.id, updates)}
                  onRemove={() => removeButton(button.id)}
                />
              ))}
              <BaseButton variant="primary" onClick={addButton}>
                <Plus size={20} />
                Add Button
              </BaseButton>
            </ButtonsContainer>
          </Section>

          <Section>
            <SectionTitle><Shield size={20} /> Safety Net (Fallback)</SectionTitle>
            <InputGroup>
              <Label>Fallback Title</Label>
              <Input
                type="text"
                value={config.fallback.title}
                onChange={(e) => updateConfig({
                  fallback: { ...config.fallback, title: e.target.value }
                })}
                placeholder="Don't wanna join yet?"
              />
            </InputGroup>

            <InputGroup>
              <Label>Fallback Image (Optional)</Label>
              <ImageUpload>
                <input
                  type="file"
                  accept="image/*,.gif"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Check file size (warn at 5MB, block at 15MB)
                      const sizeInMB = file.size / (1024 * 1024);
                      if (sizeInMB > 15) {
                        alert(`Image is too large (${sizeInMB.toFixed(1)}MB). Maximum size is 15MB.`);
                        return;
                      } else if (sizeInMB > 5) {
                        alert(`Large image (${sizeInMB.toFixed(1)}MB). Consider using a smaller image for better performance, or use an external URL.`);
                      }

                      try {
                        const imageUrl = await apiService.uploadImage(file);
                        updateConfig({
                          fallback: { ...config.fallback, imageUrl }
                        });
                      } catch (error) {
                        console.error('Fallback image upload failed:', error);
                        alert(`❌ Failed to upload fallback image: ${error instanceof Error ? error.message : 'Unknown error'}`);
                      }
                    }
                  }}
                  style={{ display: 'none' }}
                  id="fallback-image-upload"
                />
                <label htmlFor="fallback-image-upload" style={{ cursor: 'pointer' }}>
                  <Camera size={16} style={{ marginRight: '8px' }} />
                  Upload fallback image or GIF
                </label>
              </ImageUpload>
              {config.fallback.imageUrl && (
                <ImagePreview>
                  <PreviewImage src={config.fallback.imageUrl} alt="Fallback preview" />
                </ImagePreview>
              )}
            </InputGroup>
            <InputGroup>
              <Label>Fallback Button Text</Label>
              <Input
                type="text"
                value={config.fallback.buttonText}
                onChange={(e) => updateConfig({
                  fallback: { ...config.fallback, buttonText: e.target.value }
                })}
                placeholder="Get Free Trial"
              />
            </InputGroup>
            <InputGroup>
              <Label>Fallback Button Link</Label>
              <Input
                type="url"
                value={config.fallback.buttonLink}
                onChange={(e) => updateConfig({
                  fallback: { ...config.fallback, buttonLink: e.target.value }
                })}
                placeholder="https://example.com"
              />
            </InputGroup>
          </Section>

          {savedPages.length > 0 && (
            <Section>
              <SectionTitle><ClipboardList size={20} /> Your Landing Pages</SectionTitle>
              <PagesList>
                {savedPages.map((page) => (
                  <PageItem key={page.slug} isActive={page.slug === config.slug}>
                    <PageInfo onClick={async () => {
                      try {
                        const freshConfig = await apiService.loadLandingPageForEditing(page.slug);
                        if (freshConfig) {
                          setConfig(freshConfig);
                        } else {
                          setConfig(page); // fallback to cached version
                        }
                      } catch (error) {
                        console.error('Failed to load fresh config:', error);
                        setConfig(page); // fallback to cached version
                      }
                    }}>
                      <PageName>{page.name}</PageName>
                      <PageUrl>/{page.slug}</PageUrl>
                      <PageDate>Updated: {new Date(page.updatedAt).toLocaleDateString()}</PageDate>
                    </PageInfo>
                    <PageActions>
                      <BaseButton variant="success" size="small" onClick={() => window.open(`/${page.slug}`, '_blank')}>
                        <ViewIcon size={14} />
                      </BaseButton>
                      <BaseButton variant="ghost" size="small" onClick={async () => {
                        try {
                          // Fetch fresh page data with stats from backend
                          const freshPage = await apiService.loadLandingPage(page.slug);
                          if (freshPage) {
                            setSelectedStatsPage(freshPage);
                            setShowStats(true);
                          } else {
                            // Fallback to cached page if fresh load fails
                            setSelectedStatsPage(page);
                            setShowStats(true);
                          }
                        } catch (error) {
                          console.error('Failed to load fresh stats:', error);
                          // Fallback to cached page on error
                          setSelectedStatsPage(page);
                          setShowStats(true);
                        }
                      }}>
                        <BarChart3 size={14} />
                      </BaseButton>
                      <BaseButton variant="secondary" size="small" onClick={() => handleDuplicate(page.slug)}>
                        <Copy size={14} />
                      </BaseButton>
                      <BaseButton variant="danger" size="small" onClick={() => deleteConfig(page.slug)}>
                        <Trash2 size={14} />
                      </BaseButton>
                    </PageActions>
                  </PageItem>
                ))}
              </PagesList>
              <BaseButton variant="primary" size="large" onClick={createNewPage} style={{ width: '100%', justifyContent: 'center' }}>
                <Plus size={20} />
                Create New Landing Page
              </BaseButton>
            </Section>
          )}

          <Actions>
            <BaseButton variant="success" onClick={saveConfig}>
              <Save size={20} />
              Save
            </BaseButton>
            <BaseButton variant="secondary" onClick={() => setShowPreview(true)}>
              <Eye size={20} />
              Preview
            </BaseButton>
            {onLogout && (
              <BaseButton variant="danger" onClick={onLogout}>
                Logout
              </BaseButton>
            )}
          </Actions>
        </Content>
      </BuilderCard>

      {showPreview && (
        <PreviewModal
          config={config}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showStats && selectedStatsPage && (
        <StatsModal
          pageName={selectedStatsPage.name}
          buttons={selectedStatsPage.buttons}
          stats={selectedStatsPage.stats || {
            pageId: selectedStatsPage.id,
            totalVisitors: 0,
            totalPageViews: 0,
            totalClicks: 0,
            buttonPerformance: [],
            geographicData: [],
            dailyStats: []
          }}
          pageId={selectedStatsPage.id}
          onClose={() => {
            setShowStats(false);
            setSelectedStatsPage(null);
          }}
        />
      )}

      {showDuplicate && duplicateSource && (
        <DuplicateModal
          sourcePage={duplicateSource}
          allPages={savedPages}
          onClose={() => {
            setShowDuplicate(false);
            setDuplicateSource(null);
          }}
          onDuplicate={handleDuplicateSubmit}
        />
      )}
    </BuilderContainer>
  );
};

export default Builder;
